import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Camera, Upload, Edit3, Image as ImageIcon, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useCamara } from "@/components/Camara";
import { prepararArchivo } from "@/lib/imagen";
import { recargarPlanes, usePlanes } from "@/lib/ocio";
import { textoEmbarque } from "@/components/ocio/Embarque";
import { ReservaForm } from "@/components/ocio/ReservaForm";
import { useConfirmarNoClinico } from "@/components/documentos/ConfirmarNoClinico";
import { OpcionesIA } from "@/components/documentos/PreguntaSubida";
import { GastoForm } from "@/components/gastos/GastoForm";
import { eur } from "@/lib/gastos";
import { hoyISO } from "@/lib/zona";

const Opcion = ({ testid, icon: I, label, desc, onClick }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-4 bg-white border border-[#E5DFD5] rounded-2xl p-4 text-left hover:border-[#3A6B56] min-h-[72px]">
    <span className="shrink-0 w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><I size={24} className="text-[#3A6B56]" /></span>
    <span><span className="block font-heading font-bold text-lg text-[#1C2421]">{label}</span><span className="block text-sm text-[#6E7A74]">{desc}</span></span>
  </button>
);

const MOTIVOS = { archivo: "Archivo idéntico", localizador: "Mismo código de reserva", datos: "Misma fecha, lugar y título" };

const norm = (t) => String(t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s*\((ida|vuelta)\)\s*$/, "").trim();

// Vuelo ya guardado (mismo localizador o nombre, misma fecha) cuya terminal/puerta difiere de la tarjeta nueva.
const cambioEmbarque = (planes, datos) => {
  const v = (k) => datos?.[k]?.valor;
  const nueva = { terminal: v("terminal") ? String(v("terminal")) : "", puerta: v("puerta") ? String(v("puerta")) : "" };
  if (!nueva.terminal && !nueva.puerta) return null;
  const plan = planes.find((p) => p.fecha === v("fecha") && ((v("codigo") && p.codigo && norm(p.codigo) === norm(v("codigo"))) || norm(p.nombre) === norm(v("nombre"))));
  return plan && textoEmbarque(plan) !== textoEmbarque(nueva) ? { plan, nueva } : null;
};

// Añadir un plan: foto / archivo con análisis automático, o a mano. "Guardar sin análisis" no envía nada a la IA.
// docInicial: documento ya subido desde «Subir documento» que se ha reconocido como entrada o reserva.
export const OcioAdd = ({ onClose, docInicial }) => {
  const { subirDocumento, fetchAll } = useData();
  const [paso, setPaso] = useState(docInicial ? "leyendo" : "inicio");
  const [gasto, setGasto] = useState(null);
  const [doc, setDoc] = useState(null);
  const [datos, setDatos] = useState(null);
  const [conIA, setConIA] = useState(false);
  const [manual, setManual] = useState(false);
  const [sinAnalisis, setSinAnalisis] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [previo, setPrevio] = useState(null);
  const [clinico, setClinico] = useState(false);
  const [repetido, setRepetido] = useState(null);
  const [pendiente, setPendiente] = useState(null);
  const pasoPrevio = useRef("inicio");
  const navigate = useNavigate();
  const { planes } = usePlanes();
  const onFileRef = useRef(null);
  const camara = useCamara((e) => onFileRef.current(e));
  const fileInput = useRef(null);
  const noClinico = useConfirmarNoClinico();

  onFileRef.current = async (e) => {
    const elegido = e.target.files?.[0];
    e.target.value = "";
    if (!elegido) return;
    const f = await prepararArchivo(elegido);
    if (!/\.(pdf|jpe?g|png)$/i.test(f.name)) return toast.error("Formato no admitido. Usa PDF, JPG o PNG.");
    if (f.size > 10 * 1024 * 1024) return toast.error("El archivo supera el límite de 10 MB.");
    if (manual || sinAnalisis) return sinIA(f);
    pasoPrevio.current = paso;
    setPendiente(f);
    setPaso("modo");
  };
  const subir = (a, sinIA) => subirDocumento(a, { sin_ia: sinIA ? "si" : "no", ...(sinIA ? { confirma_no_clinico: "si" } : {}), nombre: a.name.replace(/\.[^.]+$/, ""), origen: "ocio" });
  const cerrado = useRef(false);
  const guardado = useRef(false);
  const analizar = async (a, existente) => {
    setPaso("leyendo");
    let d = existente || null;
    try {
      if (!d) d = await subir(a, false);
      // Cerrado durante «Analizando…»: no dejar el documento recién subido.
      if (cerrado.current) { if (!existente) api.delete(`/documentos/${d.id}`).then(() => fetchAll()).catch(() => {}); return; }
      setDoc(d);
      const { data } = await api.post(`/ocio/documentos/${d.id}/analizar`);
      if (cerrado.current) return;
      setDatos(data.datos); setConIA(true);
      const cambio = cambioEmbarque(planes, data.datos);
      if (cambio) { setPrevio(cambio); return setPaso("embarque"); }
      setPaso("form");
    } catch (err) {
      if (err.response?.status === 403) {
        if (d) api.delete(`/documentos/${d.id}`).then(() => fetchAll()).catch(() => {});
        setDoc(null);
        setClinico(true);
        return setPaso("descartado");
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer la entrada. Introduce los datos a mano.");
      setConIA(false); setPaso("form");
    }
  };
  const iniciado = useRef(false);
  useEffect(() => {
    if (!docInicial || iniciado.current) return;
    iniciado.current = true;
    analizar(null, docInicial);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const sinIA = async (a) => {
    setOcupado(true);
    try { setDoc(await subir(a, true)); setConIA(false); setPaso("form"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el archivo."); }
    setOcupado(false);
  };
  const cancelar = () => {
    cerrado.current = true;
    if (doc && !guardado.current) api.delete(`/documentos/${doc.id}`).then(() => fetchAll()).catch(() => {});
    onClose();
  };
  const actualizarEmbarque = async () => {
    setOcupado(true);
    try {
      await api.put(`/ocio/planes/${previo.plan.id}/embarque`, { ...previo.nueva, documento_id: doc?.id || null });
      await Promise.all([recargarPlanes(), fetchAll()]);
      toast.success("Datos de embarque actualizados");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar."); setOcupado(false); }
  };
  const abrirExistente = (p) => {
    if (doc) api.delete(`/documentos/${doc.id}`).then(() => fetchAll()).catch(() => {});
    onClose();
    navigate(`/ocio?plan=${p.id}`);
  };
  const guardar = async (f, vuelta, forzar = false) => {
    setOcupado(true);
    try {
      if (!forzar) {
        const { data: r } = await api.post("/ocio/planes/duplicados", { ...f, documento_id: doc?.id || null });
        if (r.duplicados.length) { setRepetido({ f, vuelta, lista: r.duplicados }); setOcupado(false); return setPaso("duplicado"); }
      }
      await api.post("/ocio/planes", { ...f, documento_id: doc?.id || null, forzar });
      if (vuelta) await api.post("/ocio/planes", { ...f, terminal: "", puerta: "", hora_llegada: "", ...vuelta, documento_id: null, aviso_2h: !!vuelta.hora, notas: "El billete está guardado en el plan de ida.", forzar: true })
        .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el plan de la vuelta."));
      guardado.current = true;
      await Promise.all([recargarPlanes(), fetchAll()]);
      toast.success("Plan guardado en Ocio y Reservas");
      // Billetes (siempre) y cualquier otra reserva con importe: ofrecer registrar también el gasto (sin inventar el importe).
      const importe = datos?.importe?.valor ?? null;
      if (f.tipo === "viaje" || Number(importe) > 0) {
        setOcupado(false);
        setGasto({ importe, fecha: hoyISO(), proveedor: f.proveedor, concepto: f.nombre, persona: f.family_member_id, form: false,
          categoria: f.tipo === "viaje" ? "viajes" : f.tipo === "hotel" ? "alojamiento" : f.tipo === "restaurante" ? "restaurantes" : "ocio" });
        return setPaso("gasto");
      }
      onClose();
    } catch (err) {
      const det = err.response?.data?.detail;
      if (det?.code === "PLAN_DUPLICADO") { setRepetido({ f, vuelta, lista: det.duplicados }); setOcupado(false); return setPaso("duplicado"); }
      toast.error(formatApiErrorDetail(det) || "No se pudo guardar el plan."); setOcupado(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="ocio-add">
      <div className="absolute inset-0 bg-[#1C2421]/40" onClick={cancelar} />
      <div className="relative bg-[#FAF8F4] w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6">
        <button data-testid="ocio-add-cerrar" aria-label="Cerrar" onClick={cancelar} className="absolute top-4 right-4 w-11 h-11 rounded-xl bg-white border border-[#E5DFD5] flex items-center justify-center"><X size={20} /></button>
        {camara.inputs}
        <input ref={fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="ocio-file-input" onChange={(e) => onFileRef.current(e)} />
        {paso === "inicio" && (
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Añadir entrada o reserva</h2>
            <div className="space-y-3 mt-5">
              <Opcion testid="ocio-opcion-foto" icon={Camera} label="Hacer una foto" desc="Fotografía la entrada o la reserva" onClick={camara.abrir} />
              {camara.esMovil && <Opcion testid="ocio-opcion-galeria" icon={ImageIcon} label="Elegir de la galería" desc="Una captura o foto que ya tengas" onClick={camara.elegirGaleria} />}
              <Opcion testid="ocio-opcion-subir" icon={Upload} label="Subir entrada o reserva" desc="PDF o imagen del dispositivo" onClick={() => fileInput.current?.click()} />
              <Opcion testid="ocio-opcion-manual" icon={Edit3} label="Introducir manualmente" desc="Escribe tú los datos" onClick={() => { setManual(true); setPaso("form"); }} />
            </div>
            <p className="text-sm text-[#6E7A74] mt-4" data-testid="ocio-transparencia-ia">AL DÍA analizará esta entrada o reserva para organizarla automáticamente.</p>
            <button data-testid="ocio-guardar-sin-analisis" onClick={() => noClinico.pedir(() => { setSinAnalisis(true); fileInput.current?.click(); })}
              className="mt-1 min-h-[44px] text-sm font-semibold text-[#4A5550] underline underline-offset-4">Guardar sin análisis</button>
            {camara.aviso}
          </div>
        )}
        {paso === "modo" && pendiente && (
          <OpcionesIA prefijo="ocio" archivo={pendiente.name} ocupado={ocupado}
            onAnalizar={() => analizar(pendiente)}
            onSinIA={() => noClinico.pedir(() => sinIA(pendiente), "esta entrada o reserva")}
            onCancelar={() => { setPendiente(null); setPaso(pasoPrevio.current); }} />
        )}
        {paso === "leyendo" && <div className="py-12 text-center" data-testid="ocio-leyendo"><Loader2 size={36} className="animate-spin mx-auto text-[#3A6B56]" /><p className="text-lg mt-4">Analizando tu entrada o reserva…</p></div>}
        {paso === "embarque" && previo && (
          <div data-testid="ocio-embarque-cambio" className="space-y-4">
            <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Este vuelo ya está guardado</h2>
            <p className="text-base text-[#4A5550]">«{previo.plan.nombre}». La tarjeta nueva trae otros datos de embarque.</p>
            <div className="bg-white border border-[#E5DFD5] rounded-xl p-4 space-y-1 text-base">
              <p data-testid="ocio-embarque-antes"><b>Guardado:</b> {textoEmbarque(previo.plan) || "sin terminal ni puerta"}</p>
              <p data-testid="ocio-embarque-nuevo"><b>Tarjeta nueva:</b> {textoEmbarque(previo.nueva)}</p>
            </div>
            {previo.plan.documento_id && doc && <p className="text-base text-[#4A5550]" data-testid="ocio-embarque-sustituir-aviso">Si actualizas, la tarjeta anterior quedará marcada como «Sustituida» en Entradas y la nueva será la vigente.</p>}
            <p className="text-sm text-[#6E7A74]">La puerta de embarque puede cambiar. Compruébala en la información de la aerolínea o del aeropuerto.</p>
            <button data-testid="ocio-embarque-actualizar" disabled={ocupado} onClick={actualizarEmbarque} className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg disabled:opacity-50">Sí, actualizar con la tarjeta nueva</button>
            <button data-testid="ocio-embarque-mantener" disabled={ocupado} onClick={cancelar} className="w-full min-h-[48px] rounded-xl border border-[#E5DFD5] bg-white text-[#1C2421] font-semibold">No, mantener lo guardado</button>
          </div>
        )}
        {paso === "descartado" && (
          <div data-testid="ocio-descartado">
            <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">{clinico ? "Este documento no se puede guardar" : "No parece una entrada o reserva"}</h2>
            <p className="text-base text-[#4A5550] mt-3" data-testid="ocio-descartado-texto">{clinico
              ? "Este documento no se puede guardar porque AL DÍA Fácil no admite documentación clínica."
              : "Este documento no se ha guardado. Puedes probar con otra foto o introducir los datos a mano."}</p>
            {!clinico && <button data-testid="ocio-descartado-manual" onClick={() => { setManual(true); setPaso("form"); }} className="w-full mt-5 min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg">Introducir manualmente</button>}
            <button data-testid="ocio-descartado-cerrar" onClick={onClose} className="w-full mt-2 min-h-[44px] text-[#4A5550] font-semibold">Cerrar</button>
          </div>
        )}
        {paso === "form" && (
          <div><ReservaForm datos={datos} conIA={conIA} docNombre={doc ? doc.filename : null} ocupado={ocupado}
            onAdjuntar={() => (manual ? noClinico.pedir(() => fileInput.current?.click()) : fileInput.current?.click())} onGuardar={guardar} onCancelar={cancelar} /></div>
        )}
        {paso === "duplicado" && repetido && (
          <div data-testid="ocio-duplicado" className="space-y-4">
            <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Posible duplicado</h2>
            <p className="text-base text-[#4A5550]" data-testid="ocio-duplicado-texto">Ya tienes un plan parecido guardado.</p>
            {repetido.lista.map((p) => (
              <div key={p.id} data-testid={`ocio-duplicado-item-${p.id}`} className="bg-white border border-[#E5DFD5] rounded-xl p-4">
                <p className="font-semibold text-[#1C2421]">{p.nombre}</p>
                <p className="text-sm font-semibold text-[#B45309]" data-testid={`ocio-duplicado-motivo-${p.id}`}>{MOTIVOS[p.motivo]}</p>
                <p className="text-sm text-[#6E7A74]">{[p.fecha.split("-").reverse().join("/"), p.hora, p.lugar].filter(Boolean).join(" · ")}</p>
                <button data-testid={`ocio-duplicado-abrir-${p.id}`} onClick={() => abrirExistente(p)} className="w-full mt-3 min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold">Abrir el existente</button>
              </div>
            ))}
            <button data-testid="ocio-duplicado-crear" disabled={ocupado} onClick={() => guardar(repetido.f, repetido.vuelta, true)}
              className="w-full min-h-[48px] rounded-xl border border-[#E5DFD5] bg-white text-[#1C2421] font-semibold disabled:opacity-50">Crear de todos modos</button>
          </div>
        )}
        {paso === "gasto" && gasto && (
          <div data-testid="ocio-gasto" className="space-y-4">
            <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">¿Lo registras también como gasto?</h2>
            <p className="text-base text-[#4A5550]" data-testid="ocio-gasto-texto">{gasto.importe != null
              ? `Este documento indica un precio de ${eur(gasto.importe)}. ¿Quieres registrarlo también en Gastos?`
              : "Este billete no indica el precio. Si quieres, puedes añadir el importe a mano y registrarlo también en Gastos."}</p>
            <button data-testid="ocio-gasto-si" onClick={() => setGasto({ ...gasto, form: true })} className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg">
              {gasto.importe != null ? "Registrar también en Gastos" : "Añadir importe y registrar gasto"}
            </button>
            <button data-testid="ocio-gasto-no" onClick={onClose} className="w-full min-h-[48px] rounded-xl border border-[#E5DFD5] bg-white text-[#1C2421] font-semibold">No es un gasto</button>
            {gasto.form && <GastoForm docId={doc?.id || null} personaInicial={gasto.persona || null} origen="ocio"
              detectado={{ fecha: { valor: gasto.fecha, confianza: 1 }, importe_total: { valor: gasto.importe, confianza: 1 }, proveedor: { valor: gasto.proveedor || null, confianza: 1 },
                concepto: { valor: gasto.concepto, confianza: 1 }, categoria: { valor: gasto.categoria, confianza: 1 } }}
              onClose={() => setGasto({ ...gasto, form: false })} onSaved={onClose} />}
          </div>
        )}
        {noClinico.dialogo}
      </div>
    </div>
  );
};
